"use strict";
// packages/shared-react/src/hooks/useWaitForConversationEvents.ts
Object.defineProperty(exports, "__esModule", { value: true });
exports.useWaitForConversationEvents = useWaitForConversationEvents;
const react_1 = require("react");
const shared_1 = require("@heartcheck/shared");
/* ---------------- Utilities ---------------- */
const ts = (s) => {
    const n = s ? Date.parse(s) : NaN;
    return Number.isFinite(n) ? n : 0;
};
function isValidEvent(e) {
    try {
        return (typeof e?.id === "string" &&
            typeof e?.timestamp === "string" &&
            typeof e?.conversationId === "string" &&
            typeof e?.sender === "string" &&
            typeof e?.agentId === "string" &&
            !!(e?.event?.message ||
                e?.event?.innerDialog ||
                e?.event?.actionRequested ||
                e?.event?.actionResult));
    }
    catch {
        return false;
    }
}
function extractGraphQLErrors(err) {
    if (!err)
        return [];
    const pools = [];
    if (Array.isArray(err?.errors))
        pools.push(err.errors);
    if (Array.isArray(err?.graphQLErrors))
        pools.push(err.graphQLErrors);
    if (Array.isArray(err?.response?.errors))
        pools.push(err.response.errors);
    if (Array.isArray(err?.data?.errors))
        pools.push(err.data.errors);
    const out = [];
    for (const arr of pools) {
        for (const e of arr || []) {
            out.push({
                message: typeof e?.message === "string" ? e.message : undefined,
                errorType: typeof e?.errorType === "string" ? e.errorType : undefined,
            });
        }
    }
    if (out.length === 0 && typeof err?.message === "string") {
        out.push({ message: err.message, errorType: undefined });
    }
    return out;
}
function isWelcomePending(err) {
    const errs = extractGraphQLErrors(err);
    for (const e of errs) {
        const m = (e.message || "").toUpperCase();
        const t = (e.errorType || "").toUpperCase();
        if (t === "WELCOME_PENDING" || m.includes("WELCOME_PENDING"))
            return true;
    }
    return false;
}
/** Build a fresh GraphQL query each time with a salt comment to defeat client/middleware caching */
function buildLoadEventsQuery(salt) {
    return new shared_1.GraphqlQuery(`
    # poll-salt: ${salt}
    query EventsByConversation($conversationId: ID!) {
      eventsByConversationId(conversationId: $conversationId) {
        id
        conversationId
        sender
        timestamp
        agentId
        userId
        event {
          message
          innerDialog
          actionRequested
          actionResult
        }
      }
    }
  `, {
        // useIdToken via the /graphql private proxy
        forwardAuthMode: "userpools",
    });
}
/* ---------------- Hook ---------------- */
function useWaitForConversationEvents({ conversationId, minEvents = 1, pollDelayMs = 750, maxAttempts = 80, trigger = 0, }) {
    const [events, setEvents] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(!!conversationId);
    const [attempt, setAttempt] = (0, react_1.useState)(0);
    const lastConvRef = (0, react_1.useRef)(null);
    const minEventsRef = (0, react_1.useRef)(minEvents);
    const delayRef = (0, react_1.useRef)(pollDelayMs);
    const maxAttemptsRef = (0, react_1.useRef)(maxAttempts);
    const attemptRef = (0, react_1.useRef)(0);
    const stoppedRef = (0, react_1.useRef)(false);
    const timerRef = (0, react_1.useRef)(null);
    const inFlightRef = (0, react_1.useRef)(false);
    (0, react_1.useEffect)(() => {
        minEventsRef.current = minEvents;
    }, [minEvents]);
    (0, react_1.useEffect)(() => {
        delayRef.current = pollDelayMs;
    }, [pollDelayMs]);
    (0, react_1.useEffect)(() => {
        maxAttemptsRef.current = maxAttempts;
    }, [maxAttempts]);
    function clearTimer() {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }
    const doSinglePoll = (0, react_1.useCallback)(async () => {
        const cid = lastConvRef.current;
        if (!cid || stoppedRef.current || inFlightRef.current)
            return;
        inFlightRef.current = true;
        try {
            const q = buildLoadEventsQuery(String(Date.now())); // defeat caches/dedupers
            const anyQ = q;
            const run = anyQ.invoke ?? anyQ.execute ?? anyQ.run ?? anyQ.fetch;
            if (typeof run !== "function") {
                throw new Error("GraphqlQuery has no supported execute method (expected invoke/execute/run/fetch).");
            }
            const resp = await run.call(q, { conversationId: cid });
            const raw = resp?.eventsByConversationId ??
                resp?.data?.eventsByConversationId ??
                [];
            // Validate & de-duplicate by id, then sort ASC by timestamp
            const valid = (Array.isArray(raw) ? raw : []).filter(isValidEvent);
            const byId = new Map();
            for (const e of valid)
                byId.set(e.id, e);
            const cleaned = Array.from(byId.values()).sort((a, b) => ts(a.timestamp) - ts(b.timestamp));
            // Advance attempt counter only on a completed poll cycle
            const n = attemptRef.current + 1;
            attemptRef.current = n;
            setAttempt(n);
            setEvents(cleaned);
            // ✅ Unlock "loading" as soon as at least one event exists
            if (cleaned.length >= 1) {
                setLoading(false);
            }
            // Stop early when we meet the required count
            if (cleaned.length >= minEventsRef.current) {
                stoppedRef.current = true;
                clearTimer();
                inFlightRef.current = false;
                return;
            }
            // Or stop on max attempts
            if (n >= maxAttemptsRef.current) {
                stoppedRef.current = true;
                clearTimer();
                inFlightRef.current = false;
                return;
            }
        }
        catch (err) {
            // If the backend explicitly signals WELCOME_PENDING, don't burn attempts; retry quickly.
            if (isWelcomePending(err)) {
                // keep attempt as-is
                clearTimer();
                timerRef.current = setTimeout(() => {
                    void doSinglePoll();
                }, 250);
                inFlightRef.current = false;
                return;
            }
            const n = attemptRef.current + 1;
            attemptRef.current = n;
            setAttempt(n);
            if (n >= maxAttemptsRef.current) {
                setLoading(false);
                stoppedRef.current = true;
                clearTimer();
                inFlightRef.current = false;
                return;
            }
        }
        // Continue polling
        clearTimer();
        timerRef.current = setTimeout(() => {
            void doSinglePoll();
        }, delayRef.current);
        inFlightRef.current = false;
    }, []);
    // Reset/start polling when chat changes
    (0, react_1.useEffect)(() => {
        if (lastConvRef.current === conversationId)
            return;
        lastConvRef.current = conversationId;
        stoppedRef.current = false;
        attemptRef.current = 0;
        setAttempt(0);
        setEvents(null);
        setLoading(!!conversationId);
        clearTimer();
        if (conversationId) {
            void doSinglePoll();
        }
        return () => {
            clearTimer();
            stoppedRef.current = true;
        };
    }, [conversationId, doSinglePoll]);
    // External nudge (e.g., responding true->false)
    (0, react_1.useEffect)(() => {
        if (trigger === 0)
            return;
        if (!conversationId)
            return;
        if (stoppedRef.current)
            return;
        void doSinglePoll();
    }, [trigger, conversationId, doSinglePoll]);
    // Stable "poll-now" for callers
    const pollNow = (0, react_1.useCallback)(() => {
        if (!conversationId)
            return;
        if (stoppedRef.current)
            return;
        if (inFlightRef.current)
            return;
        void doSinglePoll();
    }, [conversationId, doSinglePoll]);
    return (0, react_1.useMemo)(() => ({
        loading,
        events: events ?? null,
        attempt,
        pollNow,
    }), [loading, events, attempt, pollNow]);
}
